import { useState, useEffect, useCallback } from 'react';

interface TimeOptions {
  interval?: number; // Update interval in milliseconds
  format?: '12h' | '24h';
  updateSeconds?: boolean;
}

interface FormattedTime {
  hours: string;
  minutes: string;
  seconds: string;
  period: string;
  formatted: string;
  timestamp: number;
}

export function useCurrentTime(options: TimeOptions = {}): FormattedTime {
  const {
    interval = 1000,
    format = '24h',
    updateSeconds = true,
  } = options;

  const [time, setTime] = useState<Date>(new Date());

  // Format time according to options
  const formatTime = useCallback((date: Date): FormattedTime => {
    const hours24 = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const period = hours24 >= 12 ? 'PM' : 'AM';
    
    // Convert to 12-hour format if needed
    const hours12 = hours24 % 12 || 12;
    const hours = format === '12h' ? hours12 : hours24;

    // Pad numbers with leading zeros
    const paddedHours = hours.toString().padStart(2, '0');
    const paddedMinutes = minutes.toString().padStart(2, '0');
    const paddedSeconds = seconds.toString().padStart(2, '0');

    // Create formatted string
    const timeString = updateSeconds
      ? `${paddedHours}:${paddedMinutes}:${paddedSeconds}`
      : `${paddedHours}:${paddedMinutes}`;

    const formatted = format === '12h'
      ? `${timeString} ${period}`
      : timeString;

    return {
      hours: paddedHours,
      minutes: paddedMinutes,
      seconds: paddedSeconds,
      period,
      formatted,
      timestamp: date.getTime(),
    };
  }, [format, updateSeconds]);

  useEffect(() => {
    // Update immediately
    setTime(new Date());

    // Set up interval
    const timer = setInterval(() => {
      setTime(new Date());
    }, interval);

    // Clean up interval
    return () => clearInterval(timer);
  }, [interval]);

  return formatTime(time);
}

// Utility functions
export const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
};

export const getTimeDifference = (date1: Date, date2: Date = new Date()): number => {
  return Math.abs(date2.getTime() - date1.getTime());
};

export const isToday = (date: Date): boolean => {
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear();
};

export const addMinutes = (date: Date, minutes: number): Date => {
  return new Date(date.getTime() + minutes * 60000);
};

export const subtractMinutes = (date: Date, minutes: number): Date => {
  return new Date(date.getTime() - minutes * 60000);
};

export const getWeekday = (date: Date): string => {
  return date.toLocaleDateString('en-US', { weekday: 'long' });
};

export const getShortWeekday = (date: Date): string => {
  return date.toLocaleDateString('en-US', { weekday: 'short' });
};

export const getMonthName = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'long' });
};

export const getShortMonthName = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short' });
};

export default useCurrentTime;